import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DKNBellIcon, DKNMenuIcon } from "../ui/icons/DKNIcons";
import { useUnreadNotificationCount } from "../../features/notifications/hooks/useNotifications";
import { useProfile } from "../../features/profile/hooks/useProfile";
import "./TopBar.css";

const TopBar = ({ onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: profile } = useProfile();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const displayName = profile?.fullName || "";
  const avatarUrl = profile?.profilE_IMAGE_URL ?? null;

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);

  // Close menu on outside click or ESC
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  return (
    <header className="topbar">
      {/* Mobile hamburger */}
      <div className="topbar-left">
        <button
          className="topbar-hamburger topbar-icon-btn"
          onClick={onMobileMenuToggle}
          aria-label={t("nav.menu")}
        >
          <DKNMenuIcon fontSize="small" />
        </button>

        <img
          src={window.location.origin + "/logo2.png"}
          alt="DAIKING MOBILE"
          className="topbar-logo"
          onClick={() => navigate("/")}
        />
      </div>

      <div className="topbar-right">
        <div className="topbar-badge-wrap">
          <button
            className="topbar-icon-btn"
            onClick={() => navigate("/notifications")}
            aria-label={t("topbar.notifications")}
          >
            <DKNBellIcon fontSize="small" />
          </button>
          {unreadCount > 0 && (
            <span className="topbar-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </div>

        {/* Profile avatar with context menu */}
        <div className="topbar-profile-wrap" ref={menuRef}>
          <button
            className="topbar-avatar-btn"
            onClick={toggleMenu}
            aria-label={t("topbar.profile")}
            aria-expanded={menuOpen}
            aria-haspopup="true"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="topbar-avatar-img"
              />
            ) : (
              <span className="topbar-avatar-initials">
                {getInitials(displayName)}
              </span>
            )}
          </button>

          {menuOpen && (
            <div className="topbar-profile-menu" role="menu">
              <div className="topbar-profile-menu-header">
                <span className="topbar-profile-menu-name">{displayName}</span>
              </div>
              <button
                className="topbar-profile-menu-item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/profile");
                }}
              >
                {t("topbar.settings")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
